// Intent: opt-in cloud backup orchestration. HARD privacy rule — nothing leaves
// the device unless the user is logged in AND turned on "Synchronizuj z chmurą".
// Two-way last-write-wins by a device timestamp: syncNow() pulls the server copy,
// restores it if it's newer, otherwise pushes the local snapshot. Everything is
// best-effort — a sync failure never blocks the app or loses local data.

import { getDb } from '@/db';
import { pullSyncApi, pushSyncApi } from '@/api/mobile-api';
import { exportSnapshot, importSnapshot, type AppSnapshot } from '@/db/snapshot';
import { useAuthStore } from '@/store/auth-store';

const KV_CLOUD_SYNC = 'cloud_sync_enabled';
const KV_SYNC_TS = 'sync_client_updated_at';

let enabled = false;
let hydrated = false;

/** Outcome of the last sync attempt so the UI can show "udało się / kiedy". */
export type SyncResult = { at: number; ok: boolean };
let lastSync: SyncResult | null = null;

export function getLastSync(): SyncResult | null {
  return lastSync;
}

/** Load the stored toggle. Call once on boot (before the first syncNow). */
export async function initCloudSync(): Promise<void> {
  if (hydrated) return;
  hydrated = true;
  try {
    const db = await getDb();
    enabled = (await db.getKv(KV_CLOUD_SYNC)) === '1';
  } catch {
    enabled = false;
  }
}

export function cloudSyncEnabled(): boolean {
  return enabled;
}

/**
 * Auto-enable backup right after login (owner decision, 2026-07-18: the
 * president logged in and didn't know he still had to tap "Synchronizuj" —
 * login already promises "kopia w chmurze", so it should just happen).
 * Respects an explicit OFF: if the user ever flipped the toggle themselves
 * (KV holds '0'), we never re-enable behind their back.
 */
export async function enableCloudSyncAfterLogin(): Promise<void> {
  try {
    const db = await getDb();
    const stored = await db.getKv(KV_CLOUD_SYNC);
    if (stored === '0') return; // user explicitly opted out — keep it off
    if (stored === '1') {
      enabled = true;
      await syncNow();
      return;
    }
    await setCloudSyncEnabled(true);
  } catch {
    // Best-effort — login must never fail because of the backup toggle.
  }
}

/** Turn cloud backup on/off. Turning on triggers an immediate sync. */
export async function setCloudSyncEnabled(value: boolean): Promise<void> {
  enabled = value;
  try {
    const db = await getDb();
    await db.setKv(KV_CLOUD_SYNC, value ? '1' : '0');
  } catch {
    // In-memory flag still applies even if the write failed.
  }
  if (value) await syncNow();
}

/**
 * Reconcile local and server once. No-op unless enabled AND logged in (the
 * account's access token is what authorizes the call). Last-write-wins:
 *  - server newer than our last sync  -> restore server locally
 *  - otherwise                        -> push the local snapshot
 */
export async function syncNow(): Promise<void> {
  if (!enabled) return;
  if (!useAuthStore.getState().account) return;

  try {
    const db = await getDb();
    const localTs = Number((await db.getKv(KV_SYNC_TS)) ?? '0') || 0;

    const pulled = await pullSyncApi();
    const serverTs = pulled.ok && pulled.data ? pulled.data.clientUpdatedAt ?? 0 : 0;

    if (pulled.ok && pulled.data?.payload && serverTs > localTs) {
      const snapshot = parseSnapshot(pulled.data.payload);
      if (snapshot) {
        await importSnapshot(snapshot);
        await db.setKv(KV_SYNC_TS, String(serverTs));
        lastSync = { at: Date.now(), ok: true };
        return;
      }
    }

    // Local is authoritative -> back it up. Stamp a fresh device timestamp.
    const stamp = Date.now();
    const snapshot = await exportSnapshot();
    const res = await pushSyncApi(JSON.stringify(snapshot), stamp);
    if (res.ok && res.data?.accepted) {
      await db.setKv(KV_SYNC_TS, String(stamp));
    } else if (res.ok && res.data && !res.data.accepted && res.data.payload) {
      // Race: server had newer data than our stamp — restore it instead.
      const serverSnap = parseSnapshot(res.data.payload);
      if (serverSnap) {
        await importSnapshot(serverSnap);
        await db.setKv(KV_SYNC_TS, String(res.data.clientUpdatedAt ?? stamp));
      }
    }
    // Success only if the server actually answered (pull or push). Otherwise the
    // status must NOT claim a good sync — the owner explicitly wants honesty here.
    lastSync = { at: Date.now(), ok: pulled.ok || res.ok };
  } catch {
    lastSync = { at: Date.now(), ok: false };
  }
}

function parseSnapshot(raw: string): AppSnapshot | null {
  try {
    return JSON.parse(raw) as AppSnapshot;
  } catch {
    return null;
  }
}
